import { ExternalLink, Span, Text, Toggle } from "@netapp/bxp-design-system-react";
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { allMenus as i18nNamespace } from "../../../../../../i18n/namespace";
import { ReactComponent as SendButton } from "../assets/SendButton.svg";
import { ReactComponent as StopButton } from "../assets/StopButton.svg";
import { AGENT_MODES } from "../constants";
import chatStyles from "./ChatContainer.module.scss";

const ChatInput = React.memo(({ onSend, onStop, disabled = false, isStreaming = false, placeholder, initialValue = '', onInputChange }) => {
    const { t } = useTranslation(i18nNamespace);
    const [inputValue, setInputValue] = useState(initialValue);
    const [agentMode, setAgentMode] = useState(false);
    const inputRef = useRef(null);

    // Auto-resize textarea based on content
    const adjustTextareaHeight = useCallback(() => {
        const textarea = inputRef.current;
        if (textarea) {
            // Reset height to auto to get the correct scrollHeight
            textarea.style.height = 'auto';
            // Set height to scrollHeight, constrained by CSS max-height
            textarea.style.height = `${textarea.scrollHeight}px`;
        }
    }, []);

    // Adjust height when input value changes
    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue, adjustTextareaHeight]);

    // Submit handler
    const handleSubmit = useCallback((e) => {
        e?.preventDefault();
        if (inputValue.trim() && !disabled && !isStreaming) {
            const mode = agentMode ? AGENT_MODES.ACT : AGENT_MODES.DISCUSS;
            onSend(inputValue.trim(), mode);
            setInputValue('');
            onInputChange?.(''); // Clear persisted value in parent
            // Reset textarea height after clearing
            if (inputRef.current) {
                inputRef.current.style.height = 'auto';
            }
        }
    }, [inputValue, disabled, isStreaming, agentMode, onSend, onInputChange]);

    // Stop handler
    const handleStop = useCallback((e) => {
        e?.preventDefault();
        onStop?.();
    }, [onStop]);

    // Keyboard handler - Enter to submit
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    }, [handleSubmit]);

    // Input change handler
    const handleInputChange = useCallback((e) => {
        const value = e.target.value;
        setInputValue(value);
        onInputChange?.(value);
    }, [onInputChange]);

    // Check if send button should be active
    const canSend = inputValue.trim().length > 0 && !disabled && !isStreaming;

    return (
        <div className={chatStyles['chat-input-container']}>
            <div className={chatStyles['chat-input-box']}>
                <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder || t("ai_input_placeholder", "What can I do for you?")}
                    className={chatStyles['chat-input']}
                    aria-label={t("ai_input_label", "Message input")}
                />
                <div className={chatStyles['chat-input-actions']}>
                    <Toggle className={chatStyles['chat-agent-mode-toggle']} value={agentMode} toggle={() => setAgentMode(!agentMode)}>
                        <Text>Agent mode</Text>
                    </Toggle>
                    {isStreaming ? (
                        <button
                            onClick={handleStop}
                            className={chatStyles['chat-stop-button']}
                            aria-label={t("ai_stop", "Stop generation")}
                        >
                            <StopButton />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={!canSend}
                            className={classNames(chatStyles['chat-send-button'], {
                                [chatStyles['chat-send-button-active']]: canSend
                            })}
                            aria-label={t("ai_send", "Send message")}
                        >
                            <SendButton />
                        </button>
                    )}
                </div>
            </div>
            <div className={chatStyles['chat-input-footer']}>
                <Span bold color="text-secondary">
                    {t("ai_review_warning", "Review AI responses carefully.")}
                </Span>
                <ExternalLink variant="text" href="/" includeArrow={true}>
                    {t("ai_terms", "Terms and conditions")}
                </ExternalLink>
            </div>
        </div>
    );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;